import React, { useEffect, useState, useContext } from 'react';
import {
  Alert, Button, Descriptions, Segmented, Select, Spin, Table, Tag, Typography, Upload, message,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, TableOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;

const money = v => v == null
  ? '—'
  : `${Number(v) < 0 ? '-' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Money = ({ v, bold }) => (
  <span style={{
    color: Number(v) < 0 ? '#cf1322' : undefined,
    fontVariantNumeric: 'tabular-nums',
    fontWeight: bold ? 600 : undefined,
  }}>{money(v)}</span>
);

export default function PayrollGLUploadPage() {
  const currentUser = useContext(UserContext);
  // Admin alone is not enough: this is company-wide compensation data, so it
  // also requires Executive on Financial Data Access
  const isAdmin     = currentUser?.Role === 'admin'
                   && (currentUser?.CanViewFinancials ?? 0) >= 3;

  const [view,  setView]  = useState('upload');
  const [phase, setPhase] = useState('upload');   // upload | preview | done

  const [file,       setFile]       = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview,    setPreview]    = useState(null);
  const [result,     setResult]     = useState(null);

  // Browse
  const [checkDates,   setCheckDates]   = useState([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [browseDate,   setBrowseDate]   = useState(null);
  const [rows,         setRows]         = useState([]);
  const [rowsLoading,  setRowsLoading]  = useState(false);
  const [teamFilter,   setTeamFilter]   = useState([]);

  useEffect(() => {
    if (!isAdmin || view !== 'browse') return;
    setDatesLoading(true);
    api.get('/api/admin/payroll-gl/check-dates')
      .then(r => {
        const list = r.data || [];
        setCheckDates(list);
        setBrowseDate(prev => prev ?? (list.length ? list[0] : null));
      })
      .catch(() => setCheckDates([]))
      .finally(() => setDatesLoading(false));
  }, [isAdmin, view]);

  useEffect(() => {
    if (!isAdmin || view !== 'browse' || !browseDate) { setRows([]); return; }
    setRowsLoading(true);
    api.get('/api/admin/payroll-gl/rows', { params: { check_date: browseDate } })
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setRowsLoading(false));
  }, [isAdmin, view, browseDate]);

  const handlePreview = async () => {
    if (!file) { message.warning('Select a file first.'); return; }
    setPreviewing(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/admin/payroll-gl/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setPhase('preview');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to parse file.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await api.post('/api/admin/payroll-gl/confirm', { rows: preview.rows });
      setResult(res.data);
      setPhase('done');
    } catch (err) {
      message.error(err.response?.data?.error || 'Upload failed.');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => { setPhase('upload'); setFile(null); setPreview(null); setResult(null); };

  // '(No team)' is a real option rather than only a warning tag, so the rows
  // that need fixing can be isolated instead of hunted for.
  const NO_TEAM = '__none__';
  const teamOptions = [
    ...[...new Set(rows.map(r => r.TeamName).filter(Boolean))].sort()
      .map(t => ({ label: t, value: t })),
    ...(rows.some(r => !r.TeamName) ? [{ label: '(No team)', value: NO_TEAM }] : []),
  ];

  const visibleRows = teamFilter.length
    ? rows.filter(r => teamFilter.includes(r.TeamName || NO_TEAM))
    : rows;

  const rowsTotal = visibleRows.reduce((s, r) => s + (Number(r.LaborCost) || 0), 0);
  const noTeam    = visibleRows.filter(r => !r.TeamName).length;

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16, gap: 16, flexWrap: 'wrap',
      }}>
        <Title level={4} style={{ margin: 0 }}>Payroll GL</Title>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {view === 'upload' && phase !== 'upload' && (
            <Button onClick={reset}>Upload Another</Button>
          )}
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { label: 'Upload',        value: 'upload', icon: <CloudUploadOutlined /> },
              { label: 'View Uploaded', value: 'browse', icon: <TableOutlined /> },
            ]}
          />
        </div>
      </div>

      {!isAdmin && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Administrator access with Executive financial permission is required."
        />
      )}

      {/* ── UPLOAD ────────────────────────────────────────────────────────── */}
      {isAdmin && view === 'upload' && phase === 'upload' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20, maxWidth: 900 }}>
            Upload the payroll GL export. Only payroll cost lines are stored -- accounts
            of the form <strong>20-6000</strong> whose code is 6000, 6010, 6015, 6020,
            6030, 6040 or 6070. Net Pay, 401k and tax liability lines are disbursement,
            not cost, and are skipped so they cannot double-count against gross earnings.
            Re-uploading a file replaces everything already stored for the check dates it
            contains.
          </Paragraph>

          <Dragger
            name="file"
            accept=".txt,.tsv,.csv"
            beforeUpload={f => { setFile(f); return false; }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            style={{ marginBottom: 16, maxWidth: 900 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag the payroll GL export here</p>
            <p className="ant-upload-hint">Tab- or comma-delimited, with a header row containing Employee Number.</p>
          </Dragger>

          <Button type="primary" onClick={handlePreview} loading={previewing} disabled={!file}>
            Preview
          </Button>
        </>
      )}

      {/* ── PREVIEW ───────────────────────────────────────────────────────── */}
      {isAdmin && view === 'upload' && phase === 'preview' && preview && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`${preview.stats.kept} cost lines from ${preview.employees} employees across ${preview.check_dates.length} check date${preview.check_dates.length === 1 ? '' : 's'}`}
            description={
              <Descriptions size="small" column={4} style={{ marginTop: 8 }}>
                <Descriptions.Item label="Lines in file">{preview.stats.total_lines}</Descriptions.Item>
                <Descriptions.Item label="Kept">{preview.stats.kept}</Descriptions.Item>
                <Descriptions.Item label="Skipped (not a cost account)">
                  {preview.stats.skipped_account}
                </Descriptions.Item>
                <Descriptions.Item label="Check dates">
                  {preview.check_dates.join(', ')}
                </Descriptions.Item>
              </Descriptions>
            }
          />

          {preview.existing?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Existing rows will be replaced"
              description={
                <span>
                  {preview.existing.map(e =>
                    `${String(e.CheckDate).substring(0, 10)} (${e.Rows} rows)`).join(', ')}
                  {' '}already stored. Confirming deletes and reloads those check dates.
                </span>
              }
            />
          )}

          {preview.unmatched?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`${preview.unmatched.length} employee${preview.unmatched.length === 1 ? '' : 's'} not found in QAUsers`}
              description={
                <>
                  <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
                    Their cost still loads, but with no QAUsers record there is no
                    assignment history to resolve, so it will report against no team.
                    Set Payroll ID on the user record to fix.
                  </Paragraph>
                  <div style={{ maxHeight: 140, overflowY: 'auto', fontSize: 12 }}>
                    {preview.unmatched.map(u => (
                      <div key={u.EmployeeNumber}>
                        <span style={{ fontFamily: 'monospace' }}>{u.EmployeeNumber}</span>
                        {'  '}{u.FullName}{'  '}<Money v={u.Amount} />
                      </div>
                    ))}
                  </div>
                </>
              }
            />
          )}

          <Title level={5} style={{ marginBottom: 4 }}>Pay periods</Title>
          <Paragraph type="secondary" style={{ marginBottom: 8, fontSize: 12 }}>
            Derived from the check date and paygroup. Check these before confirming --
            a check shifted by a holiday will produce a period that is off by a day or
            two, which is corrected afterwards with an update rather than a re-upload.
          </Paragraph>
          <Table
            rowKey={r => `${r.CheckDate}|${r.Paygroup}`}
            dataSource={preview.periods || []}
            size="small"
            pagination={false}
            bordered
            style={{ marginBottom: 16, maxWidth: 780 }}
            columns={[
              { title: 'Check Date', dataIndex: 'CheckDate', key: 'CheckDate', width: 110 },
              { title: 'Paygroup', dataIndex: 'Paygroup', key: 'Paygroup', width: 150,
                render: v => v || <Text type="secondary">—</Text> },
              {
                title: 'Period Covered', key: 'period', width: 220,
                render: (_, r) => r.PeriodStart
                  ? `${r.PeriodStart}  to  ${r.PeriodEnd}`
                  : <Text type="danger">not derived</Text>,
              },
              {
                title: 'Rule', dataIndex: 'Rule', key: 'Rule', width: 90,
                render: v => v === 'salary'
                  ? <Tag color="purple">salary</Tag>
                  : <Tag>default</Tag>,
              },
              { title: 'Employees', dataIndex: 'Employees', key: 'Employees',
                width: 100, align: 'right' },
            ]}
          />

          <Title level={5} style={{ marginBottom: 8 }}>Totals by account</Title>
          <Table
            rowKey="AccountCode"
            dataSource={preview.by_code}
            size="small"
            pagination={false}
            bordered
            style={{ marginBottom: 16, maxWidth: 640 }}
            columns={[
              { title: 'Code',  dataIndex: 'AccountCode', key: 'AccountCode', width: 80 },
              { title: 'Account', dataIndex: 'Label', key: 'Label' },
              {
                title: 'Treated As', dataIndex: 'Classification', key: 'Classification',
                width: 110,
                render: v => v === 'cost'      ? <Tag color="blue">cost</Tag>
                           : v === 'offset'    ? <Tag color="gold">offset</Tag>
                           : <Tag color="default">liability</Tag>,
              },
              { title: 'Lines', dataIndex: 'Lines', key: 'Lines', width: 80, align: 'right' },
              { title: 'Amount', dataIndex: 'Amount', key: 'Amount', width: 130, align: 'right',
                render: v => <Money v={v} /> },
            ]}
          />

          {preview.stats.skipped_detail?.length > 0 && (
            <>
              <Title level={5} style={{ marginBottom: 8 }}>Skipped accounts</Title>
              <div style={{ marginBottom: 16, fontSize: 12, color: '#8c8c8c' }}>
                {preview.stats.skipped_detail.map(d => (
                  <Tag key={d.name} style={{ marginBottom: 4 }}>{d.name} x{d.count}</Tag>
                ))}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <Button type="primary" onClick={handleConfirm} loading={confirming}>
              Confirm Upload ({preview.stats.kept} lines)
            </Button>
            <Button onClick={reset}>Cancel</Button>
          </div>
        </>
      )}

      {/* ── DONE ──────────────────────────────────────────────────────────── */}
      {isAdmin && view === 'upload' && phase === 'done' && result && (
        <>
          <Alert
            type={result.skipped > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 20 }}
            message="Upload complete"
            description={
              <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                <Descriptions.Item label="Lines stored">{result.inserted}</Descriptions.Item>
                <Descriptions.Item label="Skipped">
                  {result.skipped > 0 ? <Tag color="red">{result.skipped}</Tag> : <Tag color="green">0</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Check dates replaced">
                  {(result.check_dates_replaced || []).join(', ')}
                </Descriptions.Item>
              </Descriptions>
            }
          />
          <Button type="primary" onClick={reset}>Upload Another</Button>
        </>
      )}

      {/* ── VIEW UPLOADED ─────────────────────────────────────────────────── */}
      {isAdmin && view === 'browse' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Text strong>Check Date:</Text>
            {datesLoading ? <Spin size="small" /> : (
              <Select
                style={{ minWidth: 160 }}
                placeholder="Select check date"
                value={browseDate}
                onChange={setBrowseDate}
                options={checkDates.map(d => ({ label: d, value: d }))}
                showSearch
                notFoundContent="Nothing uploaded yet"
              />
            )}
            {rows.length > 0 && (
              <Select
                mode="multiple"
                value={teamFilter}
                onChange={setTeamFilter}
                options={teamOptions}
                allowClear
                maxTagCount="responsive"
                placeholder="All teams"
                style={{ minWidth: 200 }}
              />
            )}
            {rows.length > 0 && (
              <>
                <Text type="secondary">{visibleRows.length} employees</Text>
                {noTeam > 0 && <Tag color="orange">{noTeam} with no team</Tag>}
                <Text strong style={{ marginLeft: 'auto' }}>
                  Labor cost: <Money v={rowsTotal} bold />
                </Text>
              </>
            )}
          </div>

          {rowsLoading ? <Spin /> : !browseDate ? (
            <Text type="secondary">Select a check date to view payroll cost.</Text>
          ) : rows.length === 0 ? (
            <Alert type="info" showIcon message={`Nothing stored for ${browseDate}.`} />
          ) : (
            <Table
              rowKey="EmployeeNumber"
              dataSource={visibleRows}
              size="small"
              pagination={false}
              bordered
              sticky
              scroll={{ x: 'max-content', y: 600 }}
              columns={[
                {
                  title: 'Employee', key: 'emp', width: 220,
                  sorter: (a, b) => (a.FullName ?? '').localeCompare(b.FullName ?? ''),
                  render: (_, r) => (
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{r.FullName || '(no name)'}</div>
                      <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                        {r.EmployeeNumber}
                      </Text>
                    </div>
                  ),
                },
                {
                  title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150,
                  // Nulls sort together rather than scattering, so the rows
                  // with no team are findable in one place
                  sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? ''),
                  render: v => v || <Tag color="orange">No team</Tag>,
                },
                { title: 'Job Title', dataIndex: 'JobTitle', key: 'JobTitle', width: 140,
                  sorter: (a, b) => (a.JobTitle ?? '').localeCompare(b.JobTitle ?? ''),
                  render: v => v || <Text type="secondary">—</Text> },
                { title: 'Paygroup', dataIndex: 'Paygroup', key: 'Paygroup', width: 130,
                  sorter: (a, b) => (a.Paygroup ?? '').localeCompare(b.Paygroup ?? ''),
                  render: v => v || <Text type="secondary">—</Text> },
                ...[
                  ['Gross',            'GrossEarnings',        110],
                  ['OT',               'Overtime',             100],
                  ['PTO',              'PTO',                  100],
                  ['Commission',       'Commission',           110],
                  ['Bonus',            'Bonus',                100],
                  ['Employer Taxes',   'EmployerTaxes',        130],
                ].map(([title, key, width]) => ({
                  title, dataIndex: key, key, width, align: 'right',
                  sorter: (a, b) => (Number(a[key]) || 0) - (Number(b[key]) || 0),
                  render: v => <Money v={v} />,
                })),
                { title: 'Labor Cost', dataIndex: 'LaborCost', key: 'LaborCost', width: 130,
                  align: 'right',
                  sorter: (a, b) => (Number(a.LaborCost) || 0) - (Number(b.LaborCost) || 0),
                  defaultSortOrder: 'descend',
                  render: v => <Money v={v} bold /> },
                {
                  title: 'Benefits (emp paid)', dataIndex: 'BenefitsEmployeePaid',
                  key: 'BenefitsEmployeePaid', width: 150, align: 'right',
                  sorter: (a, b) => (Number(a.BenefitsEmployeePaid) || 0)
                                  - (Number(b.BenefitsEmployeePaid) || 0),
                  render: v => <span style={{ color: '#8c8c8c' }}>{money(v)}</span>,
                },
                {
                  title: 'FSA (emp paid)', dataIndex: 'FSAContribution',
                  key: 'FSAContribution', width: 130, align: 'right',
                  sorter: (a, b) => (Number(a.FSAContribution) || 0)
                                  - (Number(b.FSAContribution) || 0),
                  render: v => <span style={{ color: '#8c8c8c' }}>{money(v)}</span>,
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
}
